import { Icon, MenuItem } from "@material-ui/core";
import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";

import { ActionPermissionsObject, IThemeStorageModel, IWorkspaceResourceTheme, PermissionActionType, WorkspacePlanTier, WorkspaceResourceType } from "common/interfaces";
import Banner from "js/Components/Banner";
import { Breadcrumb } from "js/Components/Breadcrumb";
import UpgradeIcon from "js/Components/UpgradeIcon";
import ThemesPaneController, { ThemesPaneControllerState } from "js/controllers/ThemesPaneController";
import WorkspaceController, { getPresentationsUsingSharedThemeCount, WorkspaceControllerState } from "js/controllers/WorkspaceController";
import AppController from "js/core/AppController";
import getLogger, { LogGroup } from "js/core/logger";
import themeManager from "js/core/themeManager";
import { pluralize } from "js/core/utilities/utilities";
import withProgressDialog from "js/core/utilities/withProgressDialog";
import { ThemeThumbnail } from "js/editor/ThemeEditor/components/ThemeThumbnail";
import { t } from "js/i18n";
import { ShowConfirmationDialog, ShowDialog, ShowErrorDialog } from "js/react/components/Dialogs/BaseDialog";
import ChangePlanDialog from "js/react/components/Dialogs/ChangePlanDialog";
import InputConfirmationDialog from "js/react/components/Dialogs/InputConfirmationDialog";
import FetchingClickShield from "js/react/components/FetchingClickShield";
import { Gap5 } from "js/react/components/Gap";
import { FlexBox } from "js/react/components/LayoutGrid";
import { NoMyResourcesThemeNotice } from "js/react/components/Notice";
import { IconMenu, RoundIconButton, UIPane } from "js/react/components/UiComponents";
import { useIntersectionObserver } from "js/react/hooks/useIntersectionObserver";
import { SearchBarContainer, UIPaneResultsContainer } from "js/react/views/AddSlide/Panes/Components/SearchBox";
import { thumbnailMenuButtonStyles } from "js/react/views/AddSlide/Panes/Components/ThumbnailCard";
import { MultiSelectThumbnailGrid, ThumbnailGridSkeleton } from "js/react/views/AddSlide/Panes/Components/ThumbnailGrid";

const logger = getLogger(LogGroup.TEAMS);

const StyledIconMenu = styled(IconMenu)`
    &&& {
        ${thumbnailMenuButtonStyles}
    }
`;

const SharedThemeThumbnailContainer = styled.div`
    min-height: 220px;
`;

function SharedThemeThumbnail(props: { resource: IWorkspaceResourceTheme, onVisibleChange: (visible: boolean, resource: IWorkspaceResourceTheme) => void }) {
    const { resource, onVisibleChange } = props;

    const containerRef = useRef<HTMLDivElement>(null);
    const [theme, setTheme] = useState<IThemeStorageModel>(null);
    const visible = useIntersectionObserver(containerRef);

    useEffect(() => {
        if (!visible) {
            return;
        }

        themeManager.getSharedTheme(resource.resourceId, true)
            .then(setTheme)
            .catch(err => {
                logger.error(err, "[TeamSharedThemesPane] Error getting shared theme");
            });

        return () => {
            setTheme(null);
        };
    }, [resource.resourceId, visible]);

    useEffect(() => {
        onVisibleChange(visible, resource);
    }, [visible]);

    const isDefault = resource.metadata.isDefault;
    const canUpgradeToManageSharedResources = WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.SHARED_RESOURCES, PermissionActionType.MANAGE);

    const handleEdit = async () => {
        const teamThemeUsageCount = await getPresentationsUsingSharedThemeCount({ workspaceId: WorkspaceController.workspaceId, themeId: resource.resourceId });

        if (teamThemeUsageCount > 0) {
            const shouldContinue = await ShowConfirmationDialog({
                title: "Editing team theme",
                message: `Any changes you make will impact ${pluralize(
                    "presentation",
                    teamThemeUsageCount,
                    "presentations"
                )} in your workspace.`,
                cancelButtonLabel: "Cancel",
                okButtonLabel: "Continue editing",
                optOutProperty: "editTeamThemeDialog"
            });

            if (!shouldContinue) {
                return;
            }
        }

        AppController.showThemeEditor({ themeId: resource.resourceId });
    };

    const handleRename = () => {
        ShowDialog(InputConfirmationDialog, {
            title: t("Rename Theme"),
            message: null,
            input: {
                value: resource.metadata.name
            },
            acceptCallback: async (name: string) => {
                try {
                    await withProgressDialog({
                        title: t("Renaming theme..."),
                        action: () => themeManager.updateThemeName(resource.resourceId, name.trim())
                    });
                } catch (err) {
                    logger.error(err, "[TeamSharedThemesPane] Error renaming theme");

                    ShowErrorDialog({
                        title: t("Unable to rename theme"),
                        message: err.message
                    });
                }
            },
        });
    };

    const handleDuplicate = async () => {
        try {
            const theme = await themeManager.getSharedTheme(resource.resourceId, true);
            await withProgressDialog({
                title: t("Duplicating theme..."),
                action: async () => {
                    const name = t("Copy of {{name}}", { name: resource.metadata.name });
                    await themeManager.createTeamTheme(theme.attributes, name);
                }
            });
        } catch (err) {
            logger.error(err, "[TeamSharedThemesPane] Error duplicating theme");

            ShowErrorDialog({
                title: t("Unable to duplicate theme"),
                message: err.message
            });
        }
    };

    const handleDelete = async () => {
        const teamThemeUsageCount = await getPresentationsUsingSharedThemeCount({ workspaceId: WorkspaceController.workspaceId, themeId: resource.resourceId });

        if (teamThemeUsageCount > 0) {
            const confirmed = await ShowConfirmationDialog({
                title: t("Are you sure you want to delete this Team Theme?"),
                message: t("Deleting this Team Theme permanently removes it from your Team Resources. Presentations currently using this theme will keep their existing design, but editors will be able to modify those themes without restriction."),
                okButtonLabel: t("Delete Team Theme"),
                buttonOptions: { acceptButtonColor: "red" }
            });

            if (!confirmed) {
                return;
            }
        }

        try {
            await withProgressDialog({
                title: t("Deleting theme..."),
                action: () => themeManager.deleteTheme(resource.resourceId)
            });
        } catch (err) {
            logger.error(err, "[TeamSharedThemesPane] Error deleting theme");
            ShowErrorDialog({
                title: t("Unable to delete theme"),
                message: err.message
            });
        }
    };

    const handleToggleHide = async () => {
        try {
            await withProgressDialog({
                title: resource.metadata.isPublished ? t("Hiding theme...") : t("Unhiding theme..."),
                action: () => themeManager.updateThemeIsPublished(resource.resourceId, !resource.metadata.isPublished)
            });
        } catch (err) {
            logger.error(err, "[TeamSharedThemesPane] Error toggling hide theme");

            ShowErrorDialog({
                title: t("Unable to toggle hide theme"),
                message: err.message
            });
        }
    };

    return (
        <SharedThemeThumbnailContainer ref={containerRef}>
            {!theme && <ThumbnailGridSkeleton />}
            {theme && <ThemeThumbnail
                item={theme}
                resource={resource}
                isDefault={isDefault}
                isDowngraded={canUpgradeToManageSharedResources}
                menu={
                    <StyledIconMenu>
                        {canUpgradeToManageSharedResources ? [
                            <MenuItem key="edit" disabled divider><UpgradeIcon /><Gap5 />{t("Edit Theme...")}</MenuItem>,
                            <MenuItem key="rename" disabled divider><UpgradeIcon /><Gap5 />{t("Rename")}</MenuItem>,
                            <MenuItem key="duplicate" disabled divider><UpgradeIcon /><Gap5 />{t("Duplicate")}</MenuItem>,
                            <hr key="divider" />,
                            ...(!resource.metadata.isDefault ? [
                                resource.metadata.isPublished
                                    ? <MenuItem key="hide" disabled divider><UpgradeIcon /><Gap5 />{t("Hide")}</MenuItem>
                                    : <MenuItem key="unhide" disabled divider><UpgradeIcon /><Gap5 />{t("Show")}</MenuItem>
                            ] : [])
                        ].filter(Boolean) : [
                            <MenuItem key="edit" onClick={() => handleEdit()}>
                                <Icon>edit</Icon>{t("Edit Theme...")}
                            </MenuItem>,
                            <MenuItem key="rename" onClick={() => handleRename()}>
                                <Icon>label</Icon>{t("Rename")}
                            </MenuItem>,
                            <MenuItem key="duplicate" onClick={() => handleDuplicate()}>
                                <Icon>content_copy</Icon>{t("Duplicate")}
                            </MenuItem>,
                            <hr key="divider" />,
                            ...(!resource.metadata.isDefault ? [
                                resource.metadata.isPublished ? (
                                    <MenuItem key="hide" onClick={() => handleToggleHide()}>
                                        <Icon>visibility_off</Icon>{t("Hide")}
                                    </MenuItem>
                                ) : (
                                    <MenuItem key="unhide" onClick={() => handleToggleHide()}>
                                        <Icon>visibility_on</Icon>{t("Show")}
                                    </MenuItem>
                                )
                            ] : [])
                        ].filter(Boolean)}
                        {!isDefault && (
                            canUpgradeToManageSharedResources ? (
                                <MenuItem key="delete" disabled divider><UpgradeIcon /><Gap5 />{t("Delete...")}</MenuItem>
                            ) : (
                                <MenuItem key="delete" onClick={() => handleDelete()}>
                                    <Icon>delete</Icon>{t("Delete...")}
                                </MenuItem>
                            )
                        )}
                    </StyledIconMenu>
                }
                onDoubleClick={canUpgradeToManageSharedResources ? undefined : (() => handleEdit())}
            />}
        </SharedThemeThumbnailContainer>
    );
}

function TeamSharedThemesPane(props: WorkspaceControllerState & ThemesPaneControllerState) {
    const { fetching, data } = props;

    const canManageSharedResources = WorkspaceController.actionPermissions[ActionPermissionsObject.SHARED_RESOURCES].manage;
    const canUpgradeToManageSharedResources = WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.SHARED_RESOURCES, PermissionActionType.MANAGE);

    const handleCreate = () => {
        AppController.showThemeEditor({ themeId: "new-team-theme" });
    };

    const handleThumbnailVisibilityChange = (visible: boolean, resource: IWorkspaceResourceTheme) => {
        if (!visible) {
            return;
        }

        if (!ThemesPaneController.canLoadMore) {
            return;
        }

        const index = data.findIndex(record => record.resource.id === resource.id);
        if (index === data.length - 1) {
            ThemesPaneController.loadMore();
        }
    };

    const isLoadingMore = fetching && data.length > 0;
    const isLoadingInitial = fetching && data.length === 0;

    return (
        <UIPane>
            <SearchBarContainer className="search-container">
                {canManageSharedResources && (
                    <>
                        <Breadcrumb>{t("Team Themes")}</Breadcrumb>
                        <RoundIconButton onClick={handleCreate}><Icon>add</Icon></RoundIconButton>
                    </>
                )}
                {canUpgradeToManageSharedResources && (
                    <FlexBox center fillWidth padding="40px 0px 0px 0px">
                        <Banner
                            headerText={t("Unlock Team Themes")}
                            bodyText={t("Upgrade your plan to keep colors, fonts, and logos consistent across presentations.")}
                            buttonLabel={t("Upgrade")}
                            onButtonClick={() => ShowDialog(ChangePlanDialog,
                                {
                                    featuredPlan: WorkspacePlanTier.TEAM,
                                    analytics: { cta: "team_themes" }
                                })
                            }
                        />
                    </FlexBox>
                )}
            </SearchBarContainer>
            <UIPaneResultsContainer style={(canUpgradeToManageSharedResources) ? { paddingTop: "20px" } : {}}>
                <FetchingClickShield visible={isLoadingInitial} backgroundColor="#EEEEEE" />
                {data.length === 0 && <NoMyResourcesThemeNotice />}
                {data.length > 0 && (
                    <MultiSelectThumbnailGrid
                        responsiveColumns
                        colGap={24}
                        rowGap={24}
                        items={data}
                        allowMultiSelect={false}
                        allowDragging={false}
                        readOnly
                        isLoadingMore={isLoadingMore}
                        renderThumbnail={({ resource }: ThemesPaneControllerState["data"][number]) => <SharedThemeThumbnail resource={resource} onVisibleChange={handleThumbnailVisibilityChange} />}
                    />
                )}
            </UIPaneResultsContainer>
        </UIPane>
    );
}

function TeamSharedThemesPaneWrapper(props: WorkspaceControllerState & ThemesPaneControllerState) {
    useEffect(() => {
        const filters: ThemesPaneControllerState["filters"] = [
            {
                field: "resource.metadata.isShared",
                value: true,
                operator: "eq"
            },
        ];

        ThemesPaneController.initialize({
            workspaceId: WorkspaceController.workspaceId,
            resourceTypes: [WorkspaceResourceType.THEME],
            sort: [{ field: "resource.metadata.isDefault", direction: "desc" }, { field: "resource.modifiedAt", direction: "desc" }],
            filters
        });

        return () => {
            ThemesPaneController.reset();
        };
    }, []);

    if (!ThemesPaneController.initialized) {
        return <UIPane><FetchingClickShield visible backgroundColor="#eee" /></UIPane>;
    }

    return <TeamSharedThemesPane {...props} />;
}

export default WorkspaceController.withInitializedState(ThemesPaneController.withState(TeamSharedThemesPaneWrapper));
